import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { groupBy } from 'lodash-es';

dayjs.extend(isBetween);

export function getDividendPayoutHistory(dividends: BaseDividend[]): DividendPayoutYear[] {
	const dividendsByYear = groupBy(dividends, (dividend) => dayjs(dividend.exDate).year());

	const dividendPayoutHistory = Object.keys(dividendsByYear)
		.sort((a, b) => Number(b) - Number(a))
		.map((year) => {
			return {
				year,
				total: dividendsByYear[year].reduce((acc, dividend) => acc + dividend.declaredRate, 0),
				dividends: dividendsByYear[year]
			};
		});

	return dividendPayoutHistory;
}

export function getDividendPayoutFyHistory(
	dividends: BaseDividend[],
	fyEndPeriod: number,
	includeForecast = true
): DividendPayoutYear[] {
	const dividendsByFiscalYear = groupBy(dividends, (dividend) =>
		getFinancialYear(dividend, fyEndPeriod)
	);

	const dividendFyPayoutHistory = Object.keys(dividendsByFiscalYear)
		.sort((a, b) => Number(b) - Number(a))
		.map((year) => ({
			year,
			total: dividendsByFiscalYear[year].reduce((acc, dividend) => acc + dividend.declaredRate, 0),
			dividends: dividendsByFiscalYear[year],
			forecast: !isCompleteFinancialYear(Number(year), fyEndPeriod)
		}))
		.filter(({ year, forecast }) => Number(year) > 2013 && (includeForecast || !forecast));

	return dividendFyPayoutHistory;
}

export function getFinancialYear(dividend: BaseDividend, fyEndPeriod: number): number {
	const comparisonYear =
		fyEndPeriod === 12
			? dayjs(dividend.announcementDate).year() - 1
			: dayjs(dividend.announcementDate).year();

	return dayjs(dividend.announcementDate)
		.subtract(2, 'months')
		.isAfter(
			dayjs()
				.set('month', fyEndPeriod - 1)
				.endOf('month')
				.set('year', comparisonYear)
		)
		? dayjs(dividend.announcementDate).year() + (fyEndPeriod === 12 ? 0 : 1)
		: dayjs(dividend.announcementDate).year() + (fyEndPeriod === 12 ? -1 : 0);
}

export function isCompleteFinancialYear(year: number, fyEndPeriod: number): boolean {
	const currentYear = dayjs().year();

	if (year > currentYear) return false;

	if (
		dayjs().isBefore(
			dayjs()
				.set('month', fyEndPeriod - 1)
				.endOf('month')
				.add(2, 'months')
				.set('year', fyEndPeriod === 12 ? year + 1 : year)
		)
	) {
		return false;
	}

	return true;
}

export function getUpcomingDividend(dividends: BaseDividend[]): BaseDividend | undefined {
	const [upcomingDividend, ...otherUpcomingDividends] = dividends.filter((dividend) =>
		dayjs(dividend.paymentDate).isAfter(dayjs())
	);

	if (!upcomingDividend) return;

	const sameDayDividends = otherUpcomingDividends.filter(
		(dividend) =>
			dayjs(dividend.exDate).isSame(dayjs(upcomingDividend.exDate)) &&
			dayjs(dividend.paymentDate).isSame(dayjs(upcomingDividend.paymentDate))
	);

	return sameDayDividends.reduce<BaseDividend>(
		(prev, current) => ({ ...prev, declaredRate: prev.declaredRate + current.declaredRate }),
		upcomingDividend
	);
}

export function consolidatedDividends(
	dividends: Populated<BaseDividend, never>[]
): Populated<BaseDividend, never>[] {
	const consolidatedDividends = Object.values(
		groupBy(dividends, (dividend) => dayjs(dividend.exDate).format('YYYY-MM-DD'))
	).map(([h, ...t]) =>
		t.reduce(
			(prev, current) => ({ ...prev, declaredRate: prev.declaredRate + current.declaredRate }),
			h
		)
	);

	return consolidatedDividends;
}

export function getTtmDividendYield(
	currentPrice: number,
	dividends: Populated<BaseDividend, never>[]
): number {
	const ttmDividends = dividends.filter((dividend) =>
		dayjs(dividend.exDate).isBetween(dayjs().subtract(1, 'year'), dayjs())
	);
	return ttmDividends.reduce((acc, dividend) => acc + dividend.declaredRate, 0) / currentPrice;
}
